import {
  WellDashboardData,
  MopTypeData,
  MopTypeValue,
  StatusCount,
  FieldProductionItem,
  RodpumpClassificationItem,
  DashboardFilterArr,
} from '../../types/WellSummaryDashboardTypes';

interface AlarmSummaryItem {
  mop: string;
  count: number;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  timestamp: string;
}

export class ApiService {
  private baseUrl: string;
  // Caches to ensure single network/dataset fetch per endpoint
  // private wellSummaryCache: WellDashboardData | null = null;
  // CHANGED: Use Map-based approach for WellDataSummary like WellSummary
  private wellDataSummaryCache: Map<string, WellDashboardData> = new Map();
  // private wellSummaryPromise: Promise<WellDashboardData | null> | null = null;
  private wellDataSummaryPromise: Map<string, Promise<WellDashboardData>> = new Map();
  private wellSummaryCache: Map<string, WellDashboardData> = new Map();
  private wellSummaryPromise: Map<string, Promise<WellDashboardData>> = new Map();

  // CRITICAL: Add call tracking to prevent infinite function invocations
  // private wellDataSummaryCallCount = 0;
  // private wellDataSummaryLastCall = 0;
  // private readonly CALL_THROTTLE_MS = 100; // Minimum 100ms between calls

  // // ✅ FIXED filtersKey — returns a unique string per filter set
  private filtersKey(filters?: Record<string, unknown>): string {
    if (!filters || typeof filters !== 'object') return '';
    return Object.keys(filters)
      .sort()
      .map((k) => `${k}:${JSON.stringify((filters as any)[k])}`)
      .join('|');
  }

  private async fetchWellSummaryOnce(filters?: Record<string, unknown>): Promise<WellDashboardData> {
    const key = this.filtersKey(filters);

    // Return cached dataset if available
    if (this.wellSummaryCache.has(key)) {
      return this.wellSummaryCache.get(key) as WellDashboardData;
    }

    // If already fetching same filters, reuse promise
    if (!this.wellSummaryPromise.has(key)) {
      this.wellSummaryPromise.set(
        key,
        (async () => {
          try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

            const resp = await fetch(`${this.baseUrl}/WellSummary`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(filters || {}),
              signal: controller.signal,
            });

            clearTimeout(timeoutId);

            if (!resp.ok) {
              // API call failed
              return { values: [], id: `wellsummary-error-${resp.status}`, dateCreated: new Date().toISOString() };
            }
            const json = await resp.json();
            const result = json && json.data && json.data.values ? json.data : json;

            return result;
          } catch (fetchError) {
            // Network error, timeout, or other exception
            const errorMessage = fetchError instanceof Error ? fetchError.message : 'unknown';
            return {
              values: [],
              id: `wellsummary-exception-${Date.now()}`,
              dateCreated: new Date().toISOString(),
              error: errorMessage,
            };
          }
        })(),
      );
    }

    const promise = this.wellSummaryPromise.get(key);
    if (promise) {
      try {
        const data = await promise;
        // Always cache the result to prevent future attempts
        this.wellSummaryCache.set(key, data);
        return data;
      } catch (promiseError) {
        // This shouldn't happen since we handle errors in the promise itself, but just in case
        const errorDataset = {
          values: [],
          id: `wellsummary-promise-error-${Date.now()}`,
          dateCreated: new Date().toISOString(),
        };
        this.wellSummaryCache.set(key, errorDataset);
        return errorDataset;
      } finally {
        this.wellSummaryPromise.delete(key);
      }
    } else {
      // Fallback case - should not normally reach here
      const fallbackDataset = { values: [], id: `wellsummary-fallback-${key}`, dateCreated: new Date().toISOString() };
      this.wellSummaryCache.set(key, fallbackDataset);
      return fallbackDataset;
    }
  }

  // Shared fetch/caching logic for WellDataSummary API
  private async fetchWellDataSummaryOnce(filters?: Record<string, unknown>): Promise<WellDashboardData> {
    // Use empty string as the cache key since WellDataSummary doesn't use filters
    // const cacheKey = '';
    // Key by filters so each filter-set has its own cache entry
    const cacheKey = this.filtersKey(filters);

    // CRITICAL: If we already have a cached result, return it immediately
    if (this.wellDataSummaryCache.has(cacheKey)) {
      return this.wellDataSummaryCache.get(cacheKey) as WellDashboardData;
    }

    // Check if we've already cached a result for this key
    if (this.wellDataSummaryCache.has(cacheKey)) {
      return this.wellDataSummaryCache.get(cacheKey) as WellDashboardData;
    }

    // CRITICAL: If there's already a promise in flight, wait for it instead of proceeding
    // This prevents race conditions when multiple Redux thunks call this method simultaneously
    if (this.wellDataSummaryPromise.has(cacheKey)) {
      const existingPromise = this.wellDataSummaryPromise.get(cacheKey);
      if (existingPromise) {
        try {
          const data = await existingPromise;
          // The promise completion should have already cached the result
          return this.wellDataSummaryCache.get(cacheKey) || data;
        } catch (error) {
          // If the existing promise failed, return a cached error dataset
          const errorDataset = {
            values: [],
            id: `welldata-race-condition-error-${Date.now()}`,
            dateCreated: new Date().toISOString(),
          };
          this.wellDataSummaryCache.set(cacheKey, errorDataset);
          return errorDataset;
        }
      }
    }

    // If no promise exists, create a new one
    if (!this.wellDataSummaryPromise.has(cacheKey)) {
      const promise = (async () => {
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

          const resp = await fetch(`${this.baseUrl}/WellDataSummary`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            // body: JSON.stringify({}),
            body: JSON.stringify(filters || {}),

            signal: controller.signal,
          });

          clearTimeout(timeoutId);

          if (!resp.ok) {
            // API call failed
            throw new Error(`HTTP ${resp.status}`);
          }

          const json = await resp.json();
          const values = json.data?.values || [];
          const applicationsIdMap: Record<string, string | null> = {};
          values.forEach((v: any) => {
            applicationsIdMap[v.mop] = v.applicationsId ?? null;
          });
          const result = json && json.data && json.data.values ? { ...json.data, applicationsIdMap } : json;

          return result;
        } catch (error) {
          // Network error, timeout, or other exception
          const errorMessage = error instanceof Error ? error.message : 'unknown';
          return {
            values: [],
            id: `welldata-exception-${Date.now()}`,
            dateCreated: new Date().toISOString(),
            error: errorMessage,
          };
        }
      })();

      this.wellDataSummaryPromise.set(cacheKey, promise);
    }

    const promise = this.wellDataSummaryPromise.get(cacheKey);
    if (promise) {
      try {
        const data = await promise;
        // Always cache the result to prevent future attempts
        this.wellDataSummaryCache.set(cacheKey, data);
        return data;
      } catch (promiseError) {
        // This shouldn't happen since we handle errors in the promise itself, but just in case
        const errorDataset = {
          values: [],
          id: `welldata-promise-error-${Date.now()}`,
          dateCreated: new Date().toISOString(),
        };
        this.wellDataSummaryCache.set(cacheKey, errorDataset);
        return errorDataset;
      } finally {
        this.wellDataSummaryPromise.delete(cacheKey);
      }
    } else {
      // Fallback case - should not normally reach here
      const fallbackDataset = {
        values: [],
        id: 'welldata-fallback',
        dateCreated: new Date().toISOString(),
      };
      this.wellDataSummaryCache.set(cacheKey, fallbackDataset);
      return fallbackDataset;
    }
  }

  public async getWellDataSummary(filters?: Record<string, unknown>): Promise<WellDashboardData> {
    return await this.fetchWellDataSummaryOnce(filters);
  }

  constructor(baseUrl = '/api') {
    this.baseUrl = baseUrl;
  }

  // Method to force clear WellDataSummary cache (for debugging memory issues)
  clearWellDataSummaryCache(): void {
    this.wellDataSummaryCache.clear();
    this.wellDataSummaryPromise.clear();
  }

  // Method to clear all caches and reset state
  clearAllCaches(): void {
    this.wellSummaryCache.clear();
    this.wellSummaryPromise.clear();
    this.wellDataSummaryCache.clear();
    this.wellDataSummaryPromise.clear();
  }

  // Fetch Alarm Summary (POST /AlarmSummary)
  async getAlarmSummary(
    filters?: DashboardFilterArr,
  ): Promise<{ success: boolean; data: AlarmSummaryItem[]; message?: string }> {
    try {
      const resp = await fetch(`${this.baseUrl}/AlarmSummary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(filters || {}),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const json = await resp.json();
      // Expect shape: { success?: boolean, data?: { totalAlarms: [{ mop, count }] } | [{mop,count}] }
      let items: unknown[] = [];
      if (Array.isArray(json)) items = json;
      else if (json?.data?.totalAlarms) items = json.data.totalAlarms;
      else if (json?.data && Array.isArray(json.data)) items = json.data;
      else if (json?.totalAlarms) items = json.totalAlarms;
      const mapped = items.map((raw) => {
        const d = raw as Record<string, unknown>;
        const mop = (d.mop || d.MOP || d.type || 'UNKNOWN') as string;
        const countCandidate = (d.count as number) ?? (d.total as number) ?? 0;
        return { mop, count: typeof countCandidate === 'number' ? countCandidate : 0 };
      });
      return { success: true, data: mapped };
    } catch (e) {
      return { success: false, data: [], message: (e as Error).message };
    }
  }

  // Get complete WellSummary data (network only, no mocks)
  async getWellDashboard(filters?: {
    mops?: string;
    businessUnits?: string;
    fields?: string;
    groups?: string;
  }): Promise<ApiResponse<WellDashboardData>> {
    /**
     * Always POST /WellSummary (filters optional), Conditional filter
     */

    try {
      const data = await this.fetchWellSummaryOnce(filters);
      return {
        success: true,
        data,
        message: this.wellSummaryCache ? 'WellSummary cache' : 'WellSummary dataset',
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      return {
        success: true, // treat as soft-success with empty dataset so other flows continue
        data: { values: [], id: 'wellsummary-empty', dateCreated: new Date().toISOString() },
        message: (error as Error).message || 'Failed to fetch WellSummary (empty fallback)',
        timestamp: new Date().toISOString(),
      };
    }
  }

  // New: fetch counts for a single status (independent column)
  async getStatusCounts(
    status: string,
    filters?: { mops?: string; businessUnits?: string; fields?: string; groups?: string },
  ): Promise<ApiResponse<{ status: string; values: { mop: string; count: number }[] }>> {
    const lower = status.toLowerCase();

    const isDataSummary = lower === 'cycles' || lower === 'nodata' || lower === 'restarted';

    // Kick off BOTH WellSummary and WellDataSummary in parallel (fire-and-forget) on first status request
    // so that subsequent status types don't incur serial latency.
    // If we have no cached entries and no in-flight wellSummary fetches, start one
    if (this.wellSummaryCache.size === 0 && this.wellSummaryPromise.size === 0) {
      this.fetchWellSummaryOnce({}).catch((error) => {
        // Silent error handling for fire-and-forget call
        console.warn('Background WellSummary fetch failed:', error);
      });
    }

    // Fire-and-forget call for WellDataSummary removed to prevent race conditions
    // The main logic below (if isDataSummary) will call fetchWellDataSummaryOnce() when needed

    // For cycles / NoData / Restarted now call real API /WellDataSummary (POST)
    if (isDataSummary) {
      try {
        // const ds = await this.fetchWellDataSummaryOnce();
        const ds = await this.fetchWellDataSummaryOnce(filters);

        // Check if we got a cached error/failed dataset - these should be treated as successful
        // to prevent Redux retries, but return empty data
        if (
          ds &&
          (ds.id?.includes('exception') ||
            ds.id?.includes('failed') ||
            ds.id?.includes('error') ||
            ds.id?.includes('circuit-open'))
        ) {
          return {
            success: true, // Return success to prevent UI retries
            data: { status, values: [] },
            message: `WellDataSummary cached error: ${ds.id}`,
            timestamp: new Date().toISOString(),
          };
        }

        // Check if we got a failed/empty dataset (but not a cached error)
        if (!ds || !Array.isArray(ds.values) || ds.values.length === 0) {
          return {
            success: true, // Return success to prevent UI retries
            data: { status, values: [] },
            message: 'WellDataSummary API failed or returned empty data',
            timestamp: new Date().toISOString(),
          };
        }

        const values = ds.values.map((m) => {
          const match = m.mopData.find((d) => {
            const dsStatus = d.status.toLowerCase();
            if (dsStatus === lower) return true;
            if (lower === 'nodata' && (dsStatus === 'nodata' || dsStatus === 'no_data')) return true;
            if (lower === 'restarted' && (dsStatus === 'restartedinlast24hours' || dsStatus === 'restarted'))
              return true;
            return false;
          });
          return { mop: m.mop, count: match ? match.count : 0 };
        });
        return {
          success: true,
          data: { status, values },
          message: 'Derived from cached WellDataSummary dataset (network)',
          timestamp: new Date().toISOString(),
        };
      } catch (e) {
        return {
          success: true, // Return success to prevent UI retries, but with empty data
          data: { status, values: [] },
          message: `WellDataSummary dataset unavailable: ${(e as Error).message}`,
          timestamp: new Date().toISOString(),
        };
      }
    }

    // Non data-summary statuses use WellSummary dataset
    try {
      const wellSummary = await this.fetchWellSummaryOnce(filters || {});

      // Check if we got a failed/empty dataset
      if (!wellSummary || !Array.isArray(wellSummary.values) || wellSummary.values.length === 0) {
        // Return empty values but mark as successful to prevent UI retries
        return {
          success: true,
          data: { status, values: [] },
          message: 'WellSummary API failed or returned empty data',
          timestamp: new Date().toISOString(),
        };
      }

      const values = wellSummary.values.map((m) => {
        const match = m.mopData.find((d) => {
          const ds = d.status.toLowerCase();
          if (ds === lower) return true;
          if (lower === 'commfail' && ds === 'commfailure') return true; // alias handling
          return false;
        });
        return { mop: m.mop, count: match ? match.count : 0 };
      });
      return {
        success: true,
        data: { status, values },
        message: 'Derived from cached WellSummary dataset (network)',
        timestamp: new Date().toISOString(),
      };
    } catch (e) {
      return {
        success: true, // Return success to prevent UI retries, but with empty data
        data: { status, values: [] },
        message: `WellSummary dataset unavailable: ${(e as Error).message}`,
        timestamp: new Date().toISOString(),
      };
    }
  }

  /**
   * Field Production (chart)
   */
  async getFieldProduction(filters?: {
    mops?: string;
    businessUnits?: string;
    fields?: string;
    groups?: string;
  }): Promise<FieldProductionItem[]> {
    const res = await fetch(`${this.baseUrl}/OilProductionSummary`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(filters || {}),
    });
    const response = await res.json();
    return response?.values ?? [];
  }

  async getRodpumpclassification(filters?: DashboardFilterArr): Promise<RodpumpClassificationItem[]> {
    const res = await fetch(`${this.baseUrl}/RodPumpClassificationSummary`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(filters || {}),
    });
    const response = await res.json();
    if (response?.summary) {
      return response.summary;
    }
    return [];
  }

  // Transform new API structure to legacy format for backward compatibility
  transformToLegacyFormat(newData: WellDashboardData): { mopTypes: { [key: string]: MopTypeData } } {
    const mopTypes: { [key: string]: MopTypeData } = {};

    if (!newData || !Array.isArray(newData.values) || newData.values.length === 0) {
      return { mopTypes }; // return empty structure if no data
    }

    newData.values.forEach((mopValue: MopTypeValue) => {
      const mopKey = mopValue.mop.toLowerCase().replace(/\s+/g, '');
      const mopData: MopTypeData = {
        name: mopKey,
        displayName: mopValue.mop,
        active: 0,
        running: 0,
        shutdown: 0,
        criticalFailure: 0,
        restarted: 0,
        cycles: 0,
        offline: 0,
        noData: 0,
        commFail: 0,
      };

      mopValue.mopData.forEach((statusData: StatusCount) => {
        const status = statusData.status.toLowerCase();
        const count = statusData.count;
        switch (status) {
          case 'active':
            mopData.active = count;
            break;
          case 'running':
            mopData.running = count;
            break;
          case 'shutdown':
            mopData.shutdown = count;
            break;
          case 'commfail':
          case 'commfailure':
            mopData.commFail = count;
            break;
          case 'offline':
            mopData.offline = count;
            break;
          case 'cycles':
            mopData.cycles = count;
            break;
          case 'nodata':
            mopData.noData = count;
            break;
          default:
            break;
        }
        // Intentionally skip any total accumulation logic
      });
      mopTypes[mopKey] = mopData;
    });
    // Return a zeroed summary without performing aggregation (aggregation removed per cleanup request)
    return { mopTypes };
  }
}

// Create a singleton instance
export const apiService = new ApiService();
